import { Supertype, supertypeClass, property } from '@haventech/amorphic';
import { Controller } from '../Controller';
import {
  MMCustomerApiUtils,
  MMPolicyInfo,
  SanderlingPolicyData,
} from '../../../../common/js/authentication/amc/MMCustomerApiUtils';
import { MMCustomerApiClient } from '../../../../common/js/authentication/amc/MMCustomerApiClient';
import { Account } from '../../../../common/js/account/Account';
import { Identity } from '../../../../common/js/account/identity/Identity';
import { Customer } from '../../../../common/js/customer/Customer';
import { CoverpathCustomer } from '../../../../coverpath-common/classes/coverpath_subclasses/CoverpathCustomer';
import { AccountAuthorization } from '../../../../common/js/account/authorization/AccountAuthorization';
import { CoverpathPolicy } from '../../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { LaunchDarklyInstance, LD_APP } from '../../../../common/js/launch-darkly/LaunchDarklySingleton';
import { FeatureFlags } from '../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { IdentityService } from '../../../../common/js/account/identity/IdentityService';
import { EmailerUtils } from '../../../../common/js/emails/EmailerUtils';
import { CoverpathBasePolicy } from '../../../../coverpath-common/classes/coverpath_subclasses/CoverpathBasePolicy';
import { OwnerUpdateResponse } from '../../../../common/js/policyAdmin/PolicyAdminUtils';
import { UICategory, UILoggerSingleton } from '../../../../common/js/logger/UILoggerSingleton';

@supertypeClass
export class SanderlingPolicyRepairController extends Supertype {
  @property({ getType: () => Controller })
  controller: Controller;

  /**
   * Feature flag for determining if we should attempt to check for and, if found, move sanderling policies
   * under the correct / current customer object
   */
  @property({ toClient: false, toServer: false })
  isSanderlingCustomerPolicyRepairEnabled = false;

  /** Mapping of what policy-numbers\product-codes exist for the logged in user from AMC*/
  mmCustomerApiPolicies: MMPolicyInfo[] = [];

  /** Array of sanderling policies to be organized under a single customer at the beginning of the auth flows*/
  filteredSanderlingPolicies: CoverpathPolicy[];

  /**
   * This customer is intentionally not being added to the session
   * the customer will be loaded onto the necessary controllers via the standard auth methods (IdentifyPrincipal)
   *
   * The usage is strictly for shifting around policy objects during the sanderling policy repair process
   */
  customer: CoverpathCustomer;

  static new(parentController) {
    return new SanderlingPolicyRepairController().init(parentController);
  }

  init(parentController) {
    this.controller = parentController;
    return this;
  }

  /**
   * If the logged in user is a heavy user, this function will perform the following steps:
   * 1. reach out to MM Customer Api to grab all associated policies - return if none are found
   * 2. filter out already claimed / invalid policies
   * 3. pull a local copy of the customer object based on the user's auth0id if it exists
   * 4. if no customer is found, a customer object is taken off one of the sanderling policies found in the next step
   * 5. Policies are moved under the correct customer object
   */
  async repairSanderlingPolicies(): Promise<void> {
    const loggerData = {
      module: 'SanderlingPolicyRepairService',
      activity: 'repairSanderlingPolicies',
      category: UICategory.request,
      data: {
        auth0Id: this.controller.auth0userId,
        amcGuid: this.controller.amcGuid,
      },
    };

    // If the user is lite and mock is NOT enabled then skip the repair
    if (!this.controller.amcGuid && !this.isMockingEnabled()) {
      UILoggerSingleton.getInstance().info(
        loggerData,
        'User is a lite user; skipping the sanderling policy repair check'
      );
      return;
    }

    // If mocking is enabled and the email does NOT contain sanderling then skip the repair
    if (this.isMockingEnabled() && this.controller.email && !this.controller.email.includes('sanderling')) {
      UILoggerSingleton.getInstance().info(loggerData, 'Email does not contain "sanderling"; skipping repair');
      return;
    }

    if (this.isMockingEnabled() && this.controller.email && this.controller.email.includes('sanderling')) {
      UILoggerSingleton.getInstance().info(loggerData, 'found mock heavy sanderling user; continuing...');
    }

    const ldInstance = await LaunchDarklyInstance.getInstance(LD_APP.cp);
    this.isSanderlingCustomerPolicyRepairEnabled = await ldInstance.getFeatureFlagValueAsBoolean(
      FeatureFlags.sanderlingPolicyRepairEnabled,
      this.controller.email
    );

    if (!this.isSanderlingCustomerPolicyRepairEnabled) {
      UILoggerSingleton.getInstance().info(
        loggerData,
        'User is a heavy but isSanderlingCustomerPolicyRepairEnabled is false; returning...'
      );
      return;
    }

    UILoggerSingleton.getInstance().info(loggerData, 'Beginning sanderling policy repair');
    // 1. get all policies listed in AMC
    await this.getHeavyUserPolicyInfo();

    if (!this.mmCustomerApiPolicies) {
      UILoggerSingleton.getInstance().info(loggerData, 'No policies returned from the customer api; returning...');
      return;
    }

    // 2. filter returned policies to see if any are unclaimed
    await this.findSanderlingPoliciesWithNoAuthorization();
    if (!this.filteredSanderlingPolicies || this.filteredSanderlingPolicies.length === 0) {
      // log that there are no available sanderling policies to migrate (all are already claimed)
      UILoggerSingleton.getInstance().info(loggerData, 'No moveable sanderling policies found; returning...');
      return;
    }

    // 3. If we find a customer, set is as the controller's customer
    await this.findExistingCustomer();

    // 4. Reorganize the the sanderling policies
    await this.moveSanderlingPolicies();

    // 5. update the customer email and notify DigiOps if necessary
    await this.repairCustomerEmail();

    // 6. Clean the controller to remove amorphic objects
    this.cleanController();

    UILoggerSingleton.getInstance().info(loggerData, 'Completed sanderling policy repair for the customer');
  }

  /**
   * Makes a request against MassMutuals customer api to grab policy information and set the found policies on this controller
   *
   * sets `this.mmCustomerApiPolicies` if there are policies found
   */
  private async getHeavyUserPolicyInfo(): Promise<void> {
    // only make these calls if we have heavy user info
    const loggerData = {
      module: 'SanderlingPolicyRepairService',
      activity: 'getHeavyUserPolicyInfo',
      category: UICategory.request,
      data: {
        auth0Id: this.controller.auth0userId,
        amcGuid: this.controller.amcGuid,
        mappings: undefined,
        policyNumbers: undefined,
      },
    };

    try {
      const ldInstance = await LaunchDarklyInstance.getInstance(LD_APP.cp);
      if (this.isMockingEnabled()) {
        // mock policies found -- return these policies to attempt a repair
        UILoggerSingleton.getInstance().info(loggerData, 'Making request against launch darkly for customer api mock');
        this.mmCustomerApiPolicies = await ldInstance.getFeatureFlagJSONValue(
          'customer-api-mock',
          this.controller.email
        );

        loggerData.data.policyNumbers =
          this.mmCustomerApiPolicies &&
          this.mmCustomerApiPolicies.length &&
          this.mmCustomerApiPolicies.map((p) => p.policyNumber).join(', ');

        UILoggerSingleton.getInstance().info(loggerData, 'Successfully pulled policies from LD');
        return;
      }
      UILoggerSingleton.getInstance().info(loggerData, 'Making request against the customer api using the AMC guid');
      const defaultSanderlingTypes: SanderlingPolicyData[] = this.amorphic.config.nconf.get('sanderlingPolicyTypes');
      const mappings: SanderlingPolicyData[] = await ldInstance.getFeatureFlagValueAsJSON(
        FeatureFlags.sanderlingPolicyTypes,
        this.controller.email,
        defaultSanderlingTypes
      );
      const response = await MMCustomerApiClient.getCustomerPolicies(this.controller.amcGuid);
      this.mmCustomerApiPolicies = MMCustomerApiUtils.extractOwnerPolicyInfo(response, mappings);
      loggerData.data.mappings = mappings && mappings.map((p) => p.productCode).join(', ');
      loggerData.data.policyNumbers =
        this.mmCustomerApiPolicies &&
        this.mmCustomerApiPolicies.length &&
        this.mmCustomerApiPolicies.map((p) => p.policyNumber).join(', ');
      UILoggerSingleton.getInstance().info(
        loggerData,
        'Successfully made request against the customer api using the AMC guid'
      );
    } catch (e) {
      const err = {
        name: e?.name,
        message: e?.message,
        stack: e?.stack,
        data: {
          auth0Id: this.controller.auth0userId,
          amcGuid: this.controller.amcGuid,
          mappings: undefined,
          policyNumbers: undefined,
          errorString: (e && e.error && e.error.message) || (e && e.message),
        },
      };
      UILoggerSingleton.getInstance().info(
        {
          module: 'SanderlingPolicyRepairService',
          activity: 'getHeavyUserPolicyInfo',
          category: UICategory.request,
          error: err,
        },
        'Failed to make request against the customer api using the AMC guid'
      );
    }
  }

  private isMockingEnabled(): boolean {
    const isMockConfigSet: boolean = this.amorphic.config.nconf.get('customer-api-mock');
    const isNonProdEnvironment = process.env.APP_ENV !== 'production';
    const isMockingEnabled = isMockConfigSet && isNonProdEnvironment;
    if (isMockingEnabled) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'SanderlingPolicyRepairService',
          activity: 'isMockingEnabled',
          category: UICategory.request,
          data: {
            isMockConfigSet,
            isNonProdEnvironment,
            isMockingEnabled,
          },
        },
        'Customer api mocking is enabled'
      );
    }
    return isMockingEnabled;
  }

  /**
   * Queries for policies based on the MM policies and filters out any already claimed policies
   *
   * Sets `this.filteredSanderlingPolicies`
   */
  private async findSanderlingPoliciesWithNoAuthorization(): Promise<void> {
    const loggerData = {
      module: 'SanderlingPolicyRepairService',
      activity: 'findSanderlingPoliciesWithNoAuthorization',
      category: UICategory.request,
      data: {
        auth0Id: this.controller.auth0userId,
        amcGuid: this.controller.amcGuid,
        filteredPolicies: undefined,
      },
    };

    try {
      const policies: CoverpathPolicy[] = await this.findSanderlingPoliciesByPolicyNumber(
        this.mmCustomerApiPolicies.map((p) => p.policyNumber)
      );

      if (!policies || policies.length === 0) {
        UILoggerSingleton.getInstance().info(loggerData, 'No policies found in the DB; returning...');
        return;
      }

      this.filteredSanderlingPolicies = await this.removePoliciesWithExistingAuthorizations(policies);

      const filteredPolicies =
        this.filteredSanderlingPolicies &&
        this.filteredSanderlingPolicies.length > 0 &&
        this.filteredSanderlingPolicies.map((policy) => policy.policyNumber).join(', ');
      loggerData.data.filteredPolicies = filteredPolicies;
      UILoggerSingleton.getInstance().info(
        loggerData,
        'Successfully filtered found sanderling policies based on account auth existence'
      );
    } catch (e) {
      const error = this.getError(e);
      UILoggerSingleton.getInstance().error({
        module: 'SanderlingPolicyRepairService',
        activity: 'findSanderlingPoliciesWithNoAuthorization',
        category: UICategory.request,
        error: {
          ...error,
          isHumanRelated: false,
          data: {
            auth0Id: this.controller.auth0userId,
            amcGuid: this.controller.amcGuid,
            filteredPolicies: undefined,
          },
        },
      });
    }
  }

  private async findSanderlingPoliciesByPolicyNumber(policyNumbers: string[]): Promise<CoverpathPolicy[]> {
    const policies = await CoverpathPolicy.persistorFetchByQuery({ policyNumber: { $in: policyNumbers } });
    return policies;
  }

  private async removePoliciesWithExistingAuthorizations(policies: CoverpathPolicy[]): Promise<CoverpathPolicy[]> {
    const customerIds: string[] = policies.map((policy: CoverpathPolicy) => {
      return policy.customer._id;
    });
    const accountAuthorizations: AccountAuthorization[] = await AccountAuthorization.persistorFetchByQuery({
      managedEntityId: { $in: customerIds },
    });
    const filteredPolicies: CoverpathPolicy[] = policies.filter((policy: CoverpathPolicy) => {
      if (!accountAuthorizations) {
        return true;
      }
      const existingAccountAuth: AccountAuthorization | undefined = accountAuthorizations.find(
        (accountAuthorization: AccountAuthorization) => accountAuthorization.managedEntityId === policy.customer._id
      );
      return !existingAccountAuth;
    });
    return filteredPolicies;
  }

  /**
   * This will use the auth0id bound to the session to find if there is a customer currently associated, and if so,
   * load that customer locally for use in policy organization
   *
   * sets `this.customer`
   */
  private async findExistingCustomer(): Promise<void> {
    let identity: Identity;
    const identityService = IdentityService.getService();
    try {
      // try to pull the account for the currently authenticated user to fetch the customer
      let customerAccount: Account = null;
      if (this.controller.auth0userId) {
        identity = await this.controller.identityService.loadIdentityByIdentifier(this.controller.auth0userId);

        if (identity) {
          customerAccount = identity.account;
        }
      }

      // if we have an account, pull the coverpath client associated account auth and load the customer
      if (customerAccount) {
        const authorizedAccount: AccountAuthorization = identityService.getAccountAuthByTemplateFromAccount(
          customerAccount,
          CoverpathCustomer.amorphicClassName
        );

        if (authorizedAccount) {
          this.customer = await Customer.persistorFetchById(authorizedAccount.managedEntityId);
          UILoggerSingleton.getInstance().info(
            {
              module: 'SanderlingPolicyRepairService',
              activity: 'findExistingCustomer',
              category: UICategory.request,
              context: {
                referenceType: 'Customer',
                referenceId: this.customer._id,
              },
              data: {
                auth0Id: this.controller.auth0userId,
                amcGuid: this.controller.amcGuid,
                customer: this.customer._id,
              },
            },
            'Account Auth Found; Successfully ran the persistor fetch for customer by managed entity id'
          );
        } else {
          UILoggerSingleton.getInstance().info(
            {
              module: 'SanderlingPolicyRepairService',
              activity: 'findExistingCustomer',
              category: UICategory.request,
              data: {
                auth0Id: this.controller.auth0userId,
                amcGuid: this.controller.amcGuid,
              },
            },
            'No account Authorization; skipping fetch for customer by managed entity id'
          );
        }
      }
    } catch (e) {
      const error = this.getError(e);
      UILoggerSingleton.getInstance().error(
        {
          module: 'SanderlingPolicyRepairService',
          activity: 'findExistingCustomer',
          category: UICategory.request,
          error: {
            ...error,
            isHumanRelated: false,
            data: {
              auth0Id: this.controller.auth0userId,
              amcGuid: this.controller.amcGuid,
            },
          },
        },
        'Failed pulling the customer starting from the auth0 identifier'
      );
    }

    // This prevents two customer objects from having the same email when a quote is being accepted AND
    // sanderling policies need to be consolidated at the same time
    if (
      this.controller &&
      this.controller.customer &&
      this.controller.customer.application &&
      this.controller.customer.application.quote
    ) {
      this.customer = this.controller.customer;
    }

    if (!this.customer) {
      this.customer = this.filteredSanderlingPolicies[0].customer;
      UILoggerSingleton.getInstance().info(
        {
          module: 'SanderlingPolicyRepairService',
          activity: 'findExistingCustomer',
          category: UICategory.request,
          context: {
            referenceType: 'Customer',
            referenceId: this.customer._id,
          },
          data: {
            auth0Id: this.controller.auth0userId,
            amcGuid: this.controller.amcGuid,
            customer: this.customer._id,
          },
        },
        'No existing customer found; defaulting to the customer on the first found sanderling policy'
      );

      // create auth objects for the user if they are a new customer to CP, but an existing sanderling customer
      if (!identity) {
        identity = await identityService.createNewIdentity(this.controller.auth0userId, true, undefined);
      }

      await identityService.associateApplicationWithIdentity(this.customer, identity);
    }
  }

  /**
   * Responsible for changing any sanderling policies in `this.filteredSanderlingPolicies` to be associated with `this.customer`
   */
  async moveSanderlingPolicies(): Promise<void> {
    const newCustomerId = this.customer._id;
    const knex = Customer.persistorGetKnex();
    const oldSanderlingCustomers = this.filteredSanderlingPolicies
      .map((filteredSanderlingPolicy) => filteredSanderlingPolicy.customer._id)
      .filter((value, index, self) => self.indexOf(value) === index);

    UILoggerSingleton.getInstance().info(
      {
        module: 'SanderlingPolicyRepairService',
        activity: 'moveSanderlingPolicies',
        category: UICategory.milestone,
        context: {
          referenceType: 'Customer',
          referenceId: newCustomerId,
        },
        data: {
          auth0Id: this.controller.auth0userId,
          amcGuid: this.controller.amcGuid,
          oldSanderlingCustomers,
          newCustomerId,
        },
      },
      `Started moving all ${oldSanderlingCustomers} to ${newCustomerId}`
    );

    for await (const oldCustomerId of oldSanderlingCustomers) {
      if (oldCustomerId !== newCustomerId) {
        try {
          UILoggerSingleton.getInstance().info(
            {
              module: 'SanderlingPolicyRepairService',
              activity: 'moveSanderlingPolicies',
              category: UICategory.milestone,
              context: {
                referenceType: 'Customer',
                referenceId: newCustomerId,
              },
              data: {
                auth0Id: this.controller.auth0userId,
                amcGuid: this.controller.amcGuid,
                oldCustomerId,
                newCustomerId,
              },
            },
            `Moving ${oldCustomerId} to ${newCustomerId}`
          );

          await Promise.all[
            (await knex.client.raw(
              `update applicant set customer_id = '${newCustomerId}' where customer_id = '${oldCustomerId}'`
            ),
            await knex.client.raw(
              `update application set customer_id = '${newCustomerId}' where customer_id = '${oldCustomerId}'`
            ),
            await knex.client.raw(
              `update phone set customer_id = '${newCustomerId}' where customer_id = '${oldCustomerId}'`
            ),
            await knex.client.raw(
              `update insurance_quotes set customer_id = '${newCustomerId}' where customer_id = '${oldCustomerId}'`
            ),
            await knex.client.raw(
              `update party set customer_id = '${newCustomerId}' where customer_id = '${oldCustomerId}'`
            ),
            await knex.client.raw(
              `update policy set customer_id = '${newCustomerId}' where customer_id = '${oldCustomerId}'`
            ),
            await knex.client.raw(
              `update address set customer_id = '${newCustomerId}' where customer_id = '${oldCustomerId}'`
            ),
            await knex.client.raw(
              `update existing_policies set customer_id = '${newCustomerId}' where customer_id = '${oldCustomerId}'`
            ))
          ];

          UILoggerSingleton.getInstance().info(
            {
              module: 'SanderlingPolicyRepairService',
              activity: 'moveSanderlingPolicies',
              category: UICategory.milestone,
              context: {
                referenceType: 'Customer',
                referenceId: newCustomerId,
              },
              data: {
                auth0Id: this.controller.auth0userId,
                amcGuid: this.controller.amcGuid,
                oldCustomerId,
                newCustomerId,
              },
            },
            `Successfully moved ${oldCustomerId} to ${newCustomerId}`
          );
        } catch (error) {
          UILoggerSingleton.getInstance().error(
            {
              module: 'SanderlingPolicyRepairService',
              activity: 'moveSanderlingPolicies',
              category: UICategory.milestone,
              context: {
                referenceType: 'Customer',
                referenceId: newCustomerId,
              },
              error: {
                ...error,
                isHumanRelated: false,
                data: {
                  auth0Id: this.controller.auth0userId,
                  amcGuid: this.controller.amcGuid,
                  oldSanderlingCustomers,
                  newCustomerId,
                },
              },
            },
            `Failed moving ${oldCustomerId} to ${newCustomerId}`
          );
        }
      }
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'SanderlingPolicyRepairService',
        activity: 'moveSanderlingPolicies',
        category: UICategory.milestone,
        context: {
          referenceType: 'Customer',
          referenceId: newCustomerId,
        },
        data: {
          auth0Id: this.controller.auth0userId,
          amcGuid: this.controller.amcGuid,
          oldSanderlingCustomers,
          newCustomerId,
        },
      },
      `Finished moving all ${oldSanderlingCustomers} to ${newCustomerId}`
    );
  }

  private async updateCustomerEmail(): Promise<void> {
    this.customer.lastName = this.controller.lastName;
    this.customer.firstName = this.controller.firstName;

    await EmailerUtils.updateEmailByCustomerId(this.customer._id, this.controller.email);

    UILoggerSingleton.getInstance().info(
      {
        module: 'SanderlingPolicyRepairService',
        function: 'repairCustomerEmail',
        category: UICategory.request,
        data: {
          auth0Id: this.controller.auth0userId,
          amcGuid: this.controller.amcGuid,
        },
      },
      'Updated the customer email'
    );
  }

  /**
   * 1. Updates the customer and owner email
   * 2. Send off notice email to CS to update PA
   */
  private async repairCustomerEmail(): Promise<void> {
    if (this.filteredSanderlingPolicies && this.filteredSanderlingPolicies.length > 0) {
      // update emails
      try {
        await this.updateCustomerEmail();

        await this.updateOwnersInPA();

        UILoggerSingleton.getInstance().info(
          {
            module: 'SanderlingPolicyRepairService',
            function: 'repairCustomerEmail',
            category: UICategory.request,
            context: {
              referenceType: 'Customer',
              referenceId: this.customer._id,
            },
            data: {
              customer: this.customer._id,
              auth0Id: this.controller.auth0userId,
              amcGuid: this.controller.amcGuid,
            },
          },
          'Updated the customer email'
        );
      } catch (e) {
        const error = this.getError(e);
        UILoggerSingleton.getInstance().error(
          {
            module: 'SanderlingPolicyRepairService',
            function: 'repairCustomerEmail',
            category: UICategory.request,
            context: {
              referenceType: 'Customer',
              referenceId: this.customer._id,
            },
            error: {
              ...error,
              isHumanRelated: false,
              data: {
                customer: this.customer._id,
                auth0Id: this.controller.auth0userId,
                amcGuid: this.controller.amcGuid,
              },
            },
          },
          'Failed updating customer emails'
        );
      }
    }
  }

  async updateOwnersInPA(): Promise<void> {
    const results = await CoverpathBasePolicy.batchUpdateOwnerEmailInPA(
      this.filteredSanderlingPolicies,
      this.controller.email
    );

    const policyNumbers = results
      .map((result) => {
        if (result.status === OwnerUpdateResponse.ErrorInPACall) {
          return result.policyNumber;
        }
      })
      .filter((policyNumber) => !!policyNumber);

    if (policyNumbers.length > 0) {
      await EmailerUtils.notifyDigitalOpsOfFailureToUpdateOwnerEmailInPA(
        this.controller.auth0userId,
        this.controller.emailer,
        this.controller,
        this.customer,
        policyNumbers
      );
    }
  }

  private cleanController(): void {
    this.customer = undefined;
    this.filteredSanderlingPolicies = undefined;
  }

  private getError(e) {
    return {
      error: e && e.error,
      code: e && e.code,
      description: (e && e.error && e.error.message) || (e && e.message),
      method: e && e.method,
    };
  }
}
